import { Preprocessing } from '@editor/services/PolylineExtraction/interfaces'
import { Vector3Like } from 'three'

import { buildSpatialHash, distSq, interleavedToVec3, neighborBucketKeys } from '../../utils/math'

// === Local utility functions (used only in DensityFilter) ================

/**
 * Compute bounding box of points
 */
const computeBounds = (pts: Vector3Like[]) => {
  if (pts.length === 0) {
    return {
      minX: 0,
      maxX: 0,
      minY: 0,
      maxY: 0,
      minZ: 0,
      maxZ: 0,
      dimensions: { x: 0, y: 0, z: 0 },
    }
  }

  const bounds = {
    minX: pts[0].x,
    maxX: pts[0].x,
    minY: pts[0].y,
    maxY: pts[0].y,
    minZ: pts[0].z,
    maxZ: pts[0].z,
  }

  for (let i = 1; i < pts.length; i += 1) {
    const p = pts[i]
    bounds.minX = Math.min(bounds.minX, p.x)
    bounds.maxX = Math.max(bounds.maxX, p.x)
    bounds.minY = Math.min(bounds.minY, p.y)
    bounds.maxY = Math.max(bounds.maxY, p.y)
    bounds.minZ = Math.min(bounds.minZ, p.z)
    bounds.maxZ = Math.max(bounds.maxZ, p.z)
  }

  return {
    ...bounds,
    dimensions: {
      x: bounds.maxX - bounds.minX,
      y: bounds.maxY - bounds.minY,
      z: bounds.maxZ - bounds.minZ,
    },
  }
}

/**
 * Convert Vector3Like[] to interleaved Float32Array [x,y,z,...]
 */
const vec3ToInterleaved = (points: Vector3Like[]): Float32Array => {
  const arr = new Float32Array(points.length * 3)
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i]
    arr[i * 3] = p.x
    arr[i * 3 + 1] = p.y
    arr[i * 3 + 2] = p.z
  }
  return arr
}

export interface DensityFilterConfig {
  /**
   * Search radius used when counting neighbors.
   * Larger = picks wider local neighborhoods; more points removed.
   */
  densityRadius?: number

  /**
   * Minimum number of neighbors required for a point to be kept.
   * Higher = more strict; removes more lone / noisy points.
   */
  minNeighbors?: number
}

/**
 * Density-based preprocessing filter
 * Removes isolated points that don't have enough neighbors within a radius
 */
export class DensityFilter extends Preprocessing {
  protected config: Required<DensityFilterConfig>

  constructor(config?: DensityFilterConfig) {
    super()
    this.config = {
      densityRadius: 0.1,
      minNeighbors: 8,
      ...config,
    }
    this.validateConfig()
  }

  getName(): string {
    return `DensityFilter(radius=${(this.config.densityRadius * 1000).toFixed(1)}mm, minNeighbors=${this.config.minNeighbors})`
  }

  run(points: Float32Array): Float32Array {
    if (points.length === 0) return points

    this.logger?.log(
      `    🔍 Density filter: radius=${this.config.densityRadius}m (${(this.config.densityRadius * 1000).toFixed(2)}mm), minNeighbors=${this.config.minNeighbors}`,
    )

    // Convert to Vector3Like for processing
    const pts = interleavedToVec3(points)

    // Analyze point cloud spatial distribution
    const bounds = computeBounds(pts)
    this.logger?.log(`    📐 Point cloud bounds (data in meters):`)
    this.logger?.log(
      `      X: ${bounds.minX.toFixed(4)} to ${bounds.maxX.toFixed(4)} (${bounds.dimensions.x.toFixed(4)}m = ${(bounds.dimensions.x * 1000).toFixed(1)}mm)`,
    )
    this.logger?.log(
      `      Y: ${bounds.minY.toFixed(4)} to ${bounds.maxY.toFixed(4)} (${bounds.dimensions.y.toFixed(4)}m = ${(bounds.dimensions.y * 1000).toFixed(1)}mm)`,
    )
    this.logger?.log(
      `      Z: ${bounds.minZ.toFixed(4)} to ${bounds.maxZ.toFixed(4)} (${bounds.dimensions.z.toFixed(4)}m = ${(bounds.dimensions.z * 1000).toFixed(1)}mm)`,
    )

    // Calculate point density
    const volume = Math.max(bounds.dimensions.x * bounds.dimensions.y * bounds.dimensions.z, 0.001)
    const density = pts.length / volume
    this.logger?.log(`    📊 Point density: ${density.toFixed(0)} points/m³ = ${(density / 1e9).toFixed(1)} points/mm³`)

    // Build spatial hash for neighbor lookup
    const cell = this.config.densityRadius
    const { map, keyOf } = buildSpatialHash(pts, cell)
    this.logger?.log(
      `    🗂️  Spatial hash: ${map.size} cells (cell size: ${cell.toFixed(6)}m = ${(cell * 1000).toFixed(2)}mm), avg ${(pts.length / map.size).toFixed(1)} points/cell`,
    )

    const filteredPoints: typeof pts = []
    let isolatedCount = 0
    let denseCount = 0

    // Sample a few points to check neighbor counts for debugging
    const sampleIndices = [
      0,
      Math.floor(pts.length * 0.25),
      Math.floor(pts.length * 0.5),
      Math.floor(pts.length * 0.75),
      pts.length - 1,
    ]
    const sampleNeighborCounts: number[] = []

    const radiusSq = this.config.densityRadius * this.config.densityRadius

    pts.forEach((p, idx) => {
      // Find neighbors using spatial hash
      const [kx, ky, kz] = keyOf(p)
        .split('_')
        .map((v) => parseInt(v, 10))
      const keys = neighborBucketKeys(kx, ky, kz)
      let neighborCount = 0

      keys.forEach((k) => {
        const arr = map.get(k)
        if (arr) {
          arr.forEach((j) => {
            if (j !== idx && distSq(pts[j], p) <= radiusSq) {
              neighborCount += 1
            }
          })
        }
      })

      // Sample neighbor counts for debugging
      if (sampleIndices.includes(idx)) {
        sampleNeighborCounts.push(neighborCount)
      }

      // Keep point if it has enough neighbors
      if (neighborCount >= this.config.minNeighbors) {
        filteredPoints.push(p)
        denseCount += 1
      } else {
        isolatedCount += 1
      }
    })

    this.logger?.log(`    📈 Neighbor count samples at [0, 25%, 50%, 75%, 100%]: [${sampleNeighborCounts.join(', ')}]`)
    this.logger?.log(`    ✂️  Filter results: ${denseCount} dense, ${isolatedCount} isolated`)
    this.logger?.log(
      `    📊 Filter effectiveness: ${((isolatedCount / pts.length) * 100).toFixed(1)}% points filtered out`,
    )

    // Warning if filter is ineffective
    if (isolatedCount === 0) {
      this.logger?.log(
        `    ⚠️  WARNING: No points filtered. Consider increasing minNeighbors (${this.config.minNeighbors}) or densityRadius (${(this.config.densityRadius * 1000).toFixed(1)}mm)`,
      )
    }

    // Convert back to interleaved format
    return vec3ToInterleaved(filteredPoints)
  }

  private validateConfig(): void {
    if (this.config.densityRadius <= 0) {
      throw new Error('densityRadius must be positive')
    }
    if (this.config.minNeighbors < 1) {
      throw new Error('minNeighbors must be at least 1')
    }
  }
}
